import { demoAccountBackend } from '../demo/accountBackend'
import { demoCustomerLogins, demoSamplePhoto as demoPhoto, demoVerifyLink as demoLink } from '../demo/hints'
import { call, hasBackend, newRequestId, nullOn } from '../core/api/http'
import { endpoints } from '../core/api/endpoints'
import type { Account, Appointment, Customer, DiaryEntry } from '../core/types/account'
import type { AccountBackend, DemoLogin, RegisterClaim } from '../core/types/backend'
import type {
  AccountDeleteRequest,
  AccountExport,
  AppointmentRequest,
  CartSyncRequest,
  CustomerPatch,
  DiaryUploadRequest,
  EmailRequest,
  EmailVerifyRequest,
  LoginRequest,
  MagicLinkVerifyRequest,
  OkResponse,
  PasswordChangeRequest,
  PasswordResetConfirmRequest,
  RegisterRequest,
  RescheduleRequest,
  SubscriptionAction,
  SubscriptionActionResponse,
  SubscriptionUndoRequest,
} from '../core/types/requests'

/**
 * Mijn omgeving ↔ backend. Componenten praten nooit rechtstreeks met deze
 * module, alleen via AccountContext. Paden: core/api/endpoints.ts. Zonder
 * backend speelt demo/accountBackend.ts de rol na.
 */

const E = endpoints.account

// Antwoord- en bodytypen komen uit core/api/endpoints.ts; hier geen generics.
const http: AccountBackend = {
  fetchMe: () => nullOn(401, call(E.me, [])),
  login: (req) => call(E.login, [], req),
  // Met een backend koppelt de server de order zelf via `claimOrderId`.
  register: (req) => call(E.register, [], req),
  sendMagicLink: (req) => call(E.magicLink, [], req),
  verifyMagicLink: (req) => call(E.magicLinkVerify, [], req),
  requestPasswordReset: (req) => call(E.passwordReset, [], req),
  confirmPasswordReset: (req) => call(E.passwordResetConfirm, [], req),
  logout: () => call(E.logout, []).catch(() => undefined),
  verifyEmail: (req) => call(E.verifyEmail, [], req),
  resendVerification: () => call(E.resendVerification, []),
  subscriptionAction: (action) => call(E.subscriptionAction, [], action),
  undoSubscription: (req) => call(E.subscriptionUndo, [], req),
  requestAppointment: (req) => call(E.requestAppointment, [], req),
  rescheduleAppointment: (id, req) => call(E.rescheduleAppointment, [id], req),
  cancelAppointment: (id) => call(E.cancelAppointment, [id]),
  uploadDiaryEntry: (req) => call(E.diaryUpload, [], req),
  deleteDiary: () => call(E.diaryDeleteAll, []),
  updateCustomer: (patch) => call(E.updateCustomer, [], patch),
  changePassword: (req) => call(E.changePassword, [], req),
  exportData: () => call(E.exportData, []),
  deleteAccount: (req) => call(E.deleteAccount, [], req),
  syncCart: (req) => call(E.cartSync, [], req),
}

const impl: AccountBackend = hasBackend ? http : demoAccountBackend

/** Alleen in de demo: voorbeeldklanten om snel in te loggen. Met een backend `null`. */
export const demoLogins: (() => DemoLogin[]) | null = hasBackend ? null : demoCustomerLogins
/** Alleen in de demo: voorbeeldfoto (als bestand) voor het fotodagboek zonder camera. Met een backend `null`: alleen echte uploads. */
export const demoSamplePhoto: (() => Promise<Blob>) | null = hasBackend ? null : demoPhoto
/** Alleen in de demo: de bevestigingslink die anders in de mail staat. Met een backend `null`. */
export const demoVerifyLink: (() => string | null) | null = hasBackend ? null : demoLink

// ——— Sessie ———

/** Het ingelogde account, of `null` als er geen (geldige) sessie is. */
export const fetchMe = async (): Promise<Account | null> => impl.fetchMe()
export const login = async (req: LoginRequest): Promise<Account> => impl.login(req)
export const sendMagicLink = async (req: EmailRequest): Promise<OkResponse> => impl.sendMagicLink(req)
/** De link in de mail: token inwisselen voor een sessie. */
export const verifyMagicLink = async (req: MagicLinkVerifyRequest): Promise<Account> => impl.verifyMagicLink(req)
export const requestPasswordReset = async (req: EmailRequest): Promise<OkResponse> => impl.requestPasswordReset(req)
/** De link uit de herstelmail (/account?herstel=<token>) met het nieuwe wachtwoord: daarna ingelogd. */
export const confirmPasswordReset = async (req: PasswordResetConfirmRequest): Promise<Account> => impl.confirmPasswordReset(req)
/** `claim` is alleen voor de demo; de server koppelt de order zelf via `claimOrderId`. */
export const register = async (req: RegisterRequest, claim?: RegisterClaim): Promise<Account> =>
  impl.register(req, claim)
export const logout = async (): Promise<void> => impl.logout()
/** De link uit de bevestigingsmail (/account?verifieer=<token>). */
export const verifyEmail = async (req: EmailVerifyRequest): Promise<OkResponse> => impl.verifyEmail(req)
export const resendVerification = async (): Promise<OkResponse> => impl.resendVerification()

// ——— Mutaties ———

/** Eén `requestId` per actie: een herhaalde aanroep (verloren antwoord) voert hem niet twee keer uit. */
export const subscriptionAction = async (action: SubscriptionAction): Promise<SubscriptionActionResponse> =>
  impl.subscriptionAction({ ...action, requestId: newRequestId() })
/** "Ongedaan maken" met het token uit het antwoord op die ene actie. */
export const undoSubscription = async (req: SubscriptionUndoRequest): Promise<SubscriptionActionResponse> =>
  impl.undoSubscription(req)
/** Ingelogd een afspraak aanvragen. Komt als aanvraag in het dossier én de inbox van Sarah. */
export const requestAppointment = async (req: AppointmentRequest): Promise<Appointment> => impl.requestAppointment(req)
export const rescheduleAppointment = async (id: string, req: RescheduleRequest): Promise<Appointment> =>
  impl.rescheduleAppointment(id, req)
export const cancelAppointment = async (id: string): Promise<void> => impl.cancelAppointment(id)
export const uploadDiaryEntry = async (req: DiaryUploadRequest): Promise<DiaryEntry> => impl.uploadDiaryEntry(req)
/** Toestemming intrekken: alle foto's weg. */
export const deleteDiary = async (): Promise<void> => impl.deleteDiary()
export const updateCustomer = async (patch: CustomerPatch): Promise<Customer> => impl.updateCustomer(patch)
/** Winkelmand op de server bewaren (mail "verlaten winkelmand"). Alleen met nieuwsbrieftoestemming. */
export const syncCart = async (req: CartSyncRequest): Promise<void> => impl.syncCart(req)
/** Wachtwoord instellen of wijzigen. De server logt daarna de andere apparaten uit. */
export const changePassword = async (req: PasswordChangeRequest): Promise<void> => impl.changePassword(req)

// ——— AVG ———

/** "Mijn gegevens downloaden". */
export const exportData = async (): Promise<AccountExport> => impl.exportData()
export const deleteAccount = async (req: AccountDeleteRequest): Promise<void> => impl.deleteAccount(req)
